"use client"

import { useQuery } from "@tanstack/react-query"
import { RoleStatus } from "@ibada/shared-types/workspace"

import { useActiveWorkspaceSlug } from "@/features/auth"
import {
  roleManagementApi,
  type ListWorkspaceRolesParams,
} from "@/features/roles/services/role-management-api"

const assignableRolesKey = (workspaceSlug: string | null | undefined) =>
  ["assignable-roles", workspaceSlug ?? "_"] as const

type AssignableRolesParams = Pick<ListWorkspaceRolesParams, "scope" | "assignmentBranchId"> & {
  enabled?: boolean
}

/**
 * Read-only role catalog for invite/edit-user assignment surfaces.
 *
 * This is intentionally separate from the role-management query path: assigning an existing role to a user is
 * not the same permission boundary as browsing the dedicated role-management surface. The backend already
 * understands this through `assignmentBranchId`; this hook keeps the frontend semantics equally explicit.
 */
export function useAssignableWorkspaceRolesQuery({
  scope,
  assignmentBranchId,
  enabled = true,
}: AssignableRolesParams) {
  const workspaceSlug = useActiveWorkspaceSlug()
  const params: ListWorkspaceRolesParams = {
    status: RoleStatus.Active,
    scope,
    ...(assignmentBranchId ? { assignmentBranchId } : {}),
  }

  return useQuery({
    queryKey: [...assignableRolesKey(workspaceSlug), params],
    queryFn: () => roleManagementApi.listRoles(params),
    enabled: Boolean(workspaceSlug) && enabled,
  })
}
